/**
 * Where things live, and why some of them are not here yet.
 *
 * Two different facts decide whether a destination appears, and they must not
 * share a mechanism:
 *
 * ```
 * availability   does this capability exist yet?   route metadata
 * capability     may this principal use it?        the decision point
 * ```
 *
 * Hiding an unbuilt destination by pretending its permission was denied would
 * make the application say *you may not* when the truth is *it is not built* —
 * and the day the backend landed, somebody would have to remember to change a
 * permission check in order to reveal a feature. So a planned capability is not
 * a destination with a flag on it. It is a different shape: no route, and no
 * permission assertion either, because for most of these the server capability
 * does not exist yet and inventing one here would predict an authorization
 * contract nobody has written.
 *
 * That difference is what makes "every destination's route resolves" checkable
 * — see `test_registry_routes_exist`. A planned entry has no `to` to be wrong.
 *
 * Activating one is deliberate and visible in a diff: implement the backend,
 * establish the server capability if the operation needs a grant, build and test
 * the screen, then change the entry's shape. No feature flag, no fake denial,
 * no dead link.
 *
 * This registry is a navigation model, not a second requirements database. The
 * authoritative inventory of the prototype is
 * `docs/architecture/web-prototype-reconciliation.md`; what is here is only
 * enough to establish the six-workspace architecture.
 */

import type { ReactNode } from "react";

import {
  AgentsIcon,
  AskIcon,
  AuditIcon,
  BrainIcon,
  ConsentIcon,
  EntitiesIcon,
  EventsIcon,
  GraphIcon,
  HomeIcon,
  KeyIcon,
  MemoryIcon,
  ReleaseIcon,
  SearchIcon,
  SystemsIcon,
  WorkIcon,
} from "./icons";

type Icon = (props: { className?: string }) => ReactNode;

/** A destination that exists: it has a route, and may need a grant to reach. */
export interface AvailableDestination {
  availability: "available";
  to: string;
  label: string;
  icon: Icon;
  /**
   * What the screen shows, for the workspace hub that offers it.
   *
   * Describes the capability as built. It is not a promise about what this
   * principal may do there — the decision point answers that, and the screen
   * asks it again on arrival.
   */
  summary: string;
  /** The capability the decision point must permit. Absent where none is needed. */
  capability?: string;
}

/**
 * A destination the architecture declares and the platform cannot yet serve.
 *
 * Deliberately carries neither a route nor a capability: it is product intent,
 * not something a principal could be permitted to do.
 */
export interface PlannedDestination {
  availability: "planned";
  key: string;
  label: string;
  icon: Icon;
}

export type Destination = AvailableDestination | PlannedDestination;

export interface NavigationGroup {
  label: string;
  items: Destination[];
}

export interface Workspace {
  key: string;
  label: string;
  /**
   * Where the workspace itself is, so it can be somewhere you go rather than a
   * heading you read. Home's workspace experience *is* Home, so it routes to
   * `/` — there is no second page describing it.
   */
  to: string;
  /** What the workspace is for. Describes the area, never what you may do in it. */
  summary: string;
  groups: NavigationGroup[];
}

/**
 * Whether a destination may be offered.
 *
 * A type guard, so the caller gets `to` and `capability` only for entries that
 * actually have them — the union does the work a runtime check would otherwise
 * be trusted to do.
 */
export function isEligible(
  destination: Destination,
  may: (capability: string) => boolean,
): destination is AvailableDestination {
  return (
    destination.availability === "available" &&
    (destination.capability === undefined || may(destination.capability))
  );
}

/**
 * The six workspaces.
 *
 * Two of them — Explore and Automate — hold nothing this deployment can serve:
 * every destination in them is planned. They are declared anyway, because the
 * architecture is real and the registry saying so is the honest record. But a
 * workspace with nothing eligible does not render, which is why they are absent
 * from the navigation rather than present and empty.
 */
export const WORKSPACES: Workspace[] = [
  {
    key: "home",
    label: "Home",
    to: "/",
    summary: "Your priorities, and what the platform is reporting to you.",
    groups: [
      {
        label: "Work",
        items: [
          {
            availability: "available",
            to: "/",
            label: "Home",
            summary: "What Nexus holds, counted from the registries that answer for it.",
            icon: HomeIcon,
          },
          { availability: "planned", key: "work", label: "My Work", icon: WorkIcon },
          { availability: "planned", key: "applications", label: "Applications", icon: SystemsIcon },
        ],
      },
    ],
  },
  {
    key: "explore",
    label: "Explore",
    to: "/workspace/explore",
    summary: "Search, ask, and the enterprise 360 views.",
    groups: [
      {
        label: "Find and ask",
        items: [
          { availability: "planned", key: "search", label: "Search", icon: SearchIcon },
          { availability: "planned", key: "ask", label: "Ask Taevas", icon: AskIcon },
        ],
      },
    ],
  },
  {
    key: "intelligence",
    label: "Intelligence",
    to: "/workspace/intelligence",
    summary: "Knowledge, memory, and the context fabric.",
    groups: [
      {
        label: "Knowledge and Memory",
        items: [
          {
            availability: "available",
            to: "/intelligence/brains",
            label: "Brains",
            summary: "Reasoning brain definitions, their versions, qualification and bindings.",
            icon: BrainIcon,
            // Reading which capabilities exist. The other six authorities are
            // asked for inside the screen, because the platform decides them
            // separately and three of them are granted to nobody by default.
            capability: "brain.read",
          },
          {
            availability: "available",
            to: "/intelligence/memory",
            label: "Memory",
            summary: "What the enterprise retains, and the revisions and withdrawals behind it.",
            icon: MemoryIcon,
            // Reading what the enterprise retains. Retaining, revising,
            // withdrawing and opening a space are each asked for inside the
            // screen, on their own keys — retaining and opening became
            // workflows once `GET /memory/spaces` made a space discoverable
            // (`NXP-MEMORY-SPACE-READ-001`).
            capability: "memory.read",
          },
          { availability: "planned", key: "personal-memory", label: "Personal Memory", icon: MemoryIcon },
        ],
      },
      {
        label: "Context Fabric",
        items: [
          {
            availability: "available",
            to: "/intelligence/context-contracts",
            label: "Context Contracts",
            summary: "Purpose-bound contexts, and the immutable rules each is assembled under.",
            icon: GraphIcon,
            // Reading which contracts exist and how each is assembled — the
            // platform guards both reads as one authority. Registering,
            // declaring and promoting are asked for nowhere, because no screen
            // offers them yet, and promotion is seeded to nobody by the
            // baseline: deciding a contract may be assembled against is a
            // different job from writing one.
            capability: "context_contract.read",
          },
        ],
      },
    ],
  },
  {
    key: "automate",
    label: "Automate",
    to: "/workspace/automate",
    summary: "Agents, skills, and governed action.",
    groups: [
      {
        label: "Design",
        items: [
          { availability: "planned", key: "agents", label: "Agents", icon: AgentsIcon },
          { availability: "planned", key: "workflows", label: "Workflows", icon: AgentsIcon },
          { availability: "planned", key: "skills", label: "Skills", icon: AgentsIcon },
        ],
      },
    ],
  },
  {
    key: "platform",
    label: "Platform",
    to: "/workspace/platform",
    summary: "Identity, systems, contracts, and the event backbone.",
    groups: [
      {
        label: "Identity and Core",
        items: [
          {
            availability: "available",
            to: "/enterprise/graph",
            label: "Enterprise Graph",
            summary: "The tiers, and governance resolved for a stated purpose, with its evidence.",
            icon: GraphIcon,
            capability: "graph.read",
          },
          {
            availability: "available",
            to: "/identity/entities",
            label: "Entities",
            summary: "The canonical registry of enterprise identity.",
            icon: EntitiesIcon,
            capability: "entity.read",
          },
          {
            availability: "available",
            to: "/identity/consent",
            label: "Consent",
            summary: "Registered purposes, and a person's answer as one of three outcomes.",
            icon: ConsentIcon,
            capability: "consent_purpose.read",
          },
          {
            availability: "available",
            to: "/identity/authorization",
            label: "Authorization",
            summary: "Roles, what they grant, and who holds them.",
            icon: AuditIcon,
            // The narrowest thing a principal must be able to do for the
            // destination to be worth offering: read which roles exist. The
            // other six are asked for separately, inside the screen, because
            // the platform decides them separately and reaching a page has
            // never been evidence of anything.
            capability: "authorization.role.read",
          },
          {
            availability: "available",
            to: "/identity/service-identities",
            label: "Service identities",
            summary: "Services that authenticate to Nexus, and the lifecycle of their credentials.",
            icon: KeyIcon,
            // Reading which services exist. Registering, issuing, rotating and
            // revoking are asked for inside the screen, each on its own. None
            // of them is authority over what a service may do — that stays on
            // Authorization, with a different administrator.
            capability: "service_identity.read",
          },
          {
            availability: "available",
            to: "/identity/access-policies",
            label: "Access policies",
            summary: "Which restricted data, such as personal data, the operations principals hold may reach.",
            icon: KeyIcon,
            // Its own destination, beside Authorization rather than inside it:
            // the access policy administrator holds these four operations and
            // not `authorization.role.read`, so a section of Authorization
            // would be unreachable to exactly the person it is for. Drafting,
            // activating and deactivating are asked for inside, each alone.
            capability: "authorization.access_policy.read",
          },
        ],
      },
      {
        label: "Systems and Contracts",
        items: [
          {
            availability: "available",
            to: "/enterprise/systems",
            label: "Connected Systems",
            summary: "What Nexus integrates with, and the connector contract behind it.",
            icon: SystemsIcon,
            capability: "system.read",
          },
        ],
      },
      {
        label: "Events and Indexes",
        items: [
          {
            availability: "available",
            to: "/operations/events",
            label: "Event Backbone",
            summary: "What the platform still owes, every delivery attempt, and redrive.",
            icon: EventsIcon,
            capability: "outbox.read",
          },
        ],
      },
    ],
  },
  {
    key: "control",
    label: "Control Center",
    to: "/workspace/control",
    summary: "Reliability, security, assurance, and release.",
    groups: [
      {
        label: "Security and Assurance",
        items: [
          {
            availability: "available",
            to: "/operations/audit",
            label: "Audit Explorer",
            summary: "The evidence trail, followed by correlation.",
            icon: AuditIcon,
            capability: "audit.read",
          },
        ],
      },
      {
        label: "Governance and Release",
        items: [
          {
            availability: "available",
            to: "/operations/releases",
            label: "Releases",
            summary: "What was deployed, and the provenance recorded with it.",
            icon: ReleaseIcon,
            capability: "release.read",
          },
        ],
      },
    ],
  },
];

/**
 * The workspaces this principal has something to do in, carrying only the
 * destinations they may reach.
 *
 * The one place eligibility is applied. The shell, the workspace hubs and the
 * breadcrumb all read the result rather than each filtering the registry
 * themselves — three implementations of "may this be offered" would eventually
 * disagree, and the one that disagreed most permissively would be the bug.
 *
 * A workspace with nothing eligible is dropped, not emptied. Rendering it would
 * advertise an area this deployment cannot serve, and doing so from a registry
 * that deliberately records unbuilt intent would turn an honest record into a
 * misleading product.
 */
export function eligibleWorkspaces(may: (capability: string) => boolean): EligibleWorkspace[] {
  return WORKSPACES.map((workspace) => ({
    ...workspace,
    groups: workspace.groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item): item is AvailableDestination => isEligible(item, may)),
      }))
      .filter((group) => group.items.length > 0),
  })).filter((workspace) => workspace.groups.length > 0);
}

/** A workspace narrowed to what one principal may reach. */
export interface EligibleWorkspace extends Omit<Workspace, "groups"> {
  groups: { label: string; items: AvailableDestination[] }[];
}

/** Whether a path is at, or inside, a destination. */
function covers(destination: string, pathname: string): boolean {
  return destination === "/" ? pathname === "/" : pathname.startsWith(destination);
}

/**
 * Where a path sits: which workspace, which cluster within it, which destination.
 *
 * Used for the breadcrumb, and derived from the registry rather than from the
 * URL's shape — the paths predate the workspaces (`/identity/authorization`
 * lives in Platform, not in an "identity" workspace) and parsing segments would
 * invent a hierarchy the product does not have.
 *
 * Eligibility is applied, so a location is only ever reported for somewhere the
 * principal may actually be. A breadcrumb naming a workspace they cannot enter
 * would leak the shape of what they were refused.
 */
export function locate(
  pathname: string,
  may: (capability: string) => boolean,
): { workspace: EligibleWorkspace; cluster: string; destination: AvailableDestination } | null {
  for (const workspace of eligibleWorkspaces(may)) {
    for (const group of workspace.groups) {
      // Longest first, so `/operations/audit/:id` resolves to Audit Explorer
      // rather than to whichever destination happened to be declared first.
      const match = [...group.items]
        .sort((a, b) => b.to.length - a.to.length)
        .find((item) => covers(item.to, pathname));
      if (match) return { workspace, cluster: group.label, destination: match };
    }
  }
  return null;
}

/** The workspace a hub route names, whether or not this principal may enter it. */
export function workspaceByKey(key: string): Workspace | undefined {
  return WORKSPACES.find((workspace) => workspace.key === key);
}
